import { DEFAULT_SCALE, DEFAULT_HORIZONTAL_SCALE } from './constants';

/**
 * Usuwa pola o wartosci undefined (Firestore ich nie akceptuje). Rekurencyjnie.
 */
const stripUndefined = (value) => {
  if (Array.isArray(value)) {
    return value.map(stripUndefined);
  }
  if (value && typeof value === 'object' && value.constructor === Object) {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v === undefined) continue;
      out[k] = stripUndefined(v);
    }
    return out;
  }
  return value;
};

/**
 * Serializuje dane projektu do formatu JSON.
 * Używane przez eksport do pliku i zapis do Firestore.
 */
export const serializeProject = ({
  boreholes,
  scale,
  horizontalScale,
  connectLayers,
  autoConnectByName,
  autoConnectMode,
  showGrid,
  showBoreholeRectangles,
  showSoilName,
  showSoilState,
  showGeotechnicalLayer,
  showGenesisAndStratigraphy,
  showThickness,
  usePastelColors,
  showWater,
  showBoreholeMeta,
  boreholeMetaOffsets,
  showElevationScaleLeft,
  showCrossSectionTitle,
  crossSectionTitle,
  crossSectionTitlePos,
  typography,
  showPolygonLabels,
  sharpenContours,
  labelFieldOrder,
  manualConnections,
  excludedAutoConnections,
  connectionPointAdjustments,
  interpolationPoints,
  drawings,
  drawingColor,
  scaleDrawingTools,
  lenses,
  terrainProfile,
  activeMeasurements,
}) => {
  return stripUndefined({
    boreholes: boreholes,
    settings: {
      scale,
      horizontalScale,
      connectLayers,
      autoConnectByName,
      autoConnectMode,
      showGrid,
      showBoreholeRectangles,
      showSoilName,
      showSoilState,
      showGeotechnicalLayer,
      showGenesisAndStratigraphy,
      showThickness,
      usePastelColors,
      showWater,
      showBoreholeMeta,
      boreholeMetaOffsets,
      showElevationScaleLeft,
      showCrossSectionTitle,
      crossSectionTitle,
      crossSectionTitlePos,
      typography,
      showPolygonLabels,
      sharpenContours,
      labelFieldOrder,
    },
    connections: {
      manualConnections,
      excludedAutoConnections,
      connectionPointAdjustments,
      interpolationPoints,
    },
    drawings,
    drawingSettings: {
      drawingColor,
      scaleDrawingTools,
    },
    lenses,
    terrainProfile: terrainProfile || null,
    activeMeasurements: activeMeasurements || [],
  });
};

/**
 * Deserializuje dane projektu z JSON.
 * Obsługuje nowy format (pełny) i stary format (tylko tablica otworów).
 * Zwraca znormalizowany obiekt z domyślnymi wartościami.
 */
export const deserializeProject = (jsonData) => {
  // Stary format - tylko tablica otworów
  if (Array.isArray(jsonData)) {
    return {
      boreholes: jsonData,
      settings: getDefaultSettings(),
      connections: getDefaultConnections(),
      drawings: [],
      drawingSettings: getDefaultDrawingSettings(),
      lenses: [],
      activeMeasurements: [],
      isLegacyFormat: true,
    };
  }

  // Nowy format
  if (jsonData.boreholes) {
    const settings = jsonData.settings || {};
    const connections = jsonData.connections || {};
    const drawingSettings = jsonData.drawingSettings || {};

    return {
      boreholes: jsonData.boreholes,
      settings: {
        scale: settings.scale || DEFAULT_SCALE,
        horizontalScale: settings.horizontalScale || DEFAULT_HORIZONTAL_SCALE,
        connectLayers: settings.connectLayers || false,
        autoConnectByName: settings.autoConnectByName || false,
        autoConnectMode: settings.autoConnectMode,
        showGrid: settings.showGrid || false,
        showBoreholeRectangles: settings.showBoreholeRectangles !== undefined ? settings.showBoreholeRectangles : true,
        showSoilName: settings.showSoilName !== undefined ? settings.showSoilName : true,
        showSoilState: settings.showSoilState || false,
        showGeotechnicalLayer: settings.showGeotechnicalLayer || false,
        showGenesisAndStratigraphy: settings.showGenesisAndStratigraphy || false,
        showThickness: settings.showThickness !== undefined ? settings.showThickness : true,
        usePastelColors: settings.usePastelColors || false,
        showWater: settings.showWater !== undefined ? settings.showWater : false,
        showPolygonLabels: settings.showPolygonLabels !== undefined ? settings.showPolygonLabels : false,
        sharpenContours: settings.sharpenContours !== undefined ? settings.sharpenContours : false,
        labelFieldOrder: Array.isArray(settings.labelFieldOrder) ? settings.labelFieldOrder : undefined,
      },
      connections: {
        manualConnections: connections.manualConnections || [],
        excludedAutoConnections: connections.excludedAutoConnections || [],
        connectionPointAdjustments: connections.connectionPointAdjustments || {},
        interpolationPoints: connections.interpolationPoints || {},
      },
      drawings: jsonData.drawings || [],
      drawingSettings: {
        drawingColor: drawingSettings.drawingColor || '#FF0000',
        scaleDrawingTools: drawingSettings.scaleDrawingTools || false,
      },
      lenses: jsonData.lenses || [],
      terrainProfile: jsonData.terrainProfile || null,
      activeMeasurements: jsonData.activeMeasurements || [],
      isLegacyFormat: false,
    };
  }

  // Nieznany format
  return null;
};

function getDefaultSettings() {
  return {
    scale: DEFAULT_SCALE,
    horizontalScale: DEFAULT_HORIZONTAL_SCALE,
    connectLayers: false,
    autoConnectByName: false,
    showGrid: false,
    showBoreholeRectangles: true,
    showSoilName: true,
    showSoilState: false,
    showGeotechnicalLayer: false,
    showGenesisAndStratigraphy: false,
    showThickness: true,
    usePastelColors: false,
    showWater: false,
  };
}

function getDefaultConnections() {
  return {
    manualConnections: [],
    excludedAutoConnections: [],
    connectionPointAdjustments: {},
    interpolationPoints: {},
  };
}

function getDefaultDrawingSettings() {
  return {
    drawingColor: '#FF0000',
    scaleDrawingTools: false,
  };
}
